// Step 2 — Org-info (T12). Gebruiker vult organisatienaam, website en
// `centralEmail` in. Updates direct op de CompagnyMeta die in Step 1 is
// aangemaakt — geen nieuwe doc.
//
// T28 — branding-fetch trigger: zodra de website-URL gevuld is en het veld
// blur, vuren we `fetchBrandAssets` Cloud Function. Resultaat (logo +
// primary/secundairy color) wordt fire-and-forget op CompagnyMeta geschreven.
// Falen blokkeert de flow nooit — gebruiker kan handmatig invullen via Settings.

import { useContext, useEffect, useRef, useState } from 'react'
import firebase from '../../../firebase/compat'

import { OnboardingFlow as OnboardingFlowContext } from '../../../StateManagment/OnboardingFlow'
import { db } from '../../../firebase/config'
import useDevPreview from '../../../hooks/useDevPreview'

const Step2OrgInfo = ({ onNext, onPrev }) => {

    // Context
    const {
        compagnyId,
        formState,
        setFormState,
        modules,
        partner,
    } = useContext(OnboardingFlowContext)

    const brandName = partner?.name || 'Deccos'

    // Form state — herstellen bij terugnavigatie via context
    const orgFromContext = formState.org || {}
    const [communityName, setCommunityName] = useState(orgFromContext.communityName || '')
    const [website, setWebsite] = useState(orgFromContext.website || '')
    const [centralEmail, setCentralEmail] = useState(orgFromContext.centralEmail || '')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    // Branding-fetch (T28) — async, fire-and-forget. Visueel feedback zodat
    // gebruiker weet dat we 'm even pakken.
    const [brandFetching, setBrandFetching] = useState(false)
    const [brandFetched, setBrandFetched] = useState(false)
    const lastFetchedUrlRef = useRef('')

    // Dev-preview: bypass alle Firestore-writes (zie useDevPreview hook)
    const { active: devPreview } = useDevPreview()

    const fetchBranding = async (url) => {

        // Triviale guards — geen URL, dev-mode, of al opgehaald voor dezelfde URL
        if (!url || !url.trim()) return
        if (devPreview) return
        if (!compagnyId) return
        if (lastFetchedUrlRef.current === url) return

        lastFetchedUrlRef.current = url
        setBrandFetching(true)

        try {
            const fetchBrandAssets = firebase.functions().httpsCallable('fetchBrandAssets')
            const result = await fetchBrandAssets({ website: url })

            const { logo, primaryColor, secundairyColor } = result.data || {}

            // Geen bruikbare data → niet schrijven, geen fout. Gebruiker kan
            // later via Settings zelf invullen.
            const update = {}
            if (logo) update.Logo = logo
            if (primaryColor) update.PrimaryColor = primaryColor
            if (secundairyColor) update.SecundairyColor = secundairyColor

            if (Object.keys(update).length === 0) {
                setBrandFetching(false)
                return
            }

            const snap = await db.collection('CompagnyMeta')
                .where('CompagnyID', '==', compagnyId)
                .limit(1)
                .get()

            if (!snap.empty) {
                await snap.docs[0].ref.update(update)
                setBrandFetched(true)
            }
        } catch (err) {
            console.warn('Step2OrgInfo branding-fetch faalde', err?.message || err)
            // Geen UI-error — fire-and-forget. Gebruiker merkt niets.
        } finally {
            setBrandFetching(false)
        }

    }

    const handleWebsiteBlur = () => {
        fetchBranding(website)
    }

    // Defensive: als gebruiker direct op /signup/2 belandt zonder Step 1
    // doorlopen te hebben, geen compagnyId → waarschuw. In dev-mode is een
    // ontbrekende compagnyId OK want we schrijven niets.
    useEffect(() => {
        if (!compagnyId && !devPreview) {
            setError('We hebben je account-gegevens niet kunnen vinden. Ga terug naar Step 1 om te beginnen.')
        }
    }, [compagnyId, devPreview])

    const submit = async (e) => {
        e.preventDefault()
        setError('')

        // Dev-preview: skip ALLE validatie + Firestore-writes.
        if (devPreview) {
            setFormState((prev) => ({
                ...prev,
                org: { communityName, website, centralEmail },
            }))
            onNext()
            return
        }

        if (!communityName) {
            setError('Vul de naam van je organisatie in.')
            return
        }

        if (!compagnyId) {
            setError('Geen actieve onboarding-sessie. Begin opnieuw bij Step 1.')
            return
        }

        setLoading(true)

        try {
            // Volgende lastStep: 3 als impactMeten gekozen, anders 4.
            const nextStep = modules.impactMeten ? 3 : 4

            // Vind de CompagnyMeta-doc (createProjectAccount gebruikte .doc().set
            // met auto-id, dus we querien op CompagnyID-veld).
            const snap = await db.collection('CompagnyMeta')
                .where('CompagnyID', '==', compagnyId)
                .limit(1)
                .get()

            if (snap.empty) {
                throw new Error('CompagnyMeta niet gevonden voor deze sessie.')
            }

            const update = {
                CommunityName: communityName,
                Website: website,
                CentralEmail: centralEmail,
                OnboardingStatus: { lastStep: nextStep, completed: false },
            }

            await snap.docs[0].ref.update(update)

            setFormState((prev) => ({
                ...prev,
                org: { communityName, website, centralEmail },
            }))

            onNext()

        } catch (err) {
            console.error('Step2OrgInfo submit error', err)
            setError(err.message || 'Er ging iets mis. Probeer het opnieuw.')
            setLoading(false)
        }
    }

    return (
        <div className="onboarding-step">
            <h1>Vertel ons over je organisatie</h1>
            <p className="onboarding-step-intro">
                Deze informatie gebruiken we voor je profiel en — als je website-adres ingevuld is — om alvast wat context op te halen.
            </p>

            <form className="onboarding-form" onSubmit={submit}>
                <div className="onboarding-field">
                    <label>Naam van de organisatie *</label>
                    <input
                        type="text"
                        value={communityName}
                        onChange={(e) => setCommunityName(e.target.value)}
                        placeholder="Bijv. Stichting Voorbeeld"
                    />
                </div>

                <div className="onboarding-field">
                    <label>Website</label>
                    <input
                        type="text"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        onBlur={handleWebsiteBlur}
                        placeholder="https://"
                    />
                    {brandFetching && (
                        <p className="onboarding-field-hint">
                            We pakken alvast je logo en huisstijl-kleuren op...
                        </p>
                    )}
                    {brandFetched && !brandFetching && (
                        <p className="onboarding-field-hint">
                            ✓ Logo en kleuren opgehaald. Je kunt ze later aanpassen via Instellingen.
                        </p>
                    )}
                </div>

                <div className="onboarding-field">
                    <label>Centraal e-mailadres</label>
                    <input
                        type="email"
                        value={centralEmail}
                        onChange={(e) => setCentralEmail(e.target.value)}
                        placeholder="info@organisatie.nl"
                    />
                    <p className="onboarding-field-hint">
                        Apart van je login-adres. Hier komen meldingen vanuit {brandName} binnen.
                    </p>
                </div>

                {error && <p className="onboarding-error">{error}</p>}

                <div className="onboarding-step-actions">
                    <button
                        type="button"
                        className="onboarding-button-secondary"
                        onClick={onPrev}
                    >
                        Terug
                    </button>
                    <button type="submit" disabled={loading}>
                        {loading ? <span className="onboarding-button-spinner" aria-label="Bezig" /> : 'Volgende'}
                    </button>
                </div>
            </form>
        </div>
    )

}

export default Step2OrgInfo
